import React, { Component, ErrorInfo, ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { styled } from '@linaria/react'
import { FormattedMessage } from 'react-intl'
import { Typography } from '../../ui/Typography'
import { colors } from '../../styles/colors'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  text-align: center;
  background: var(--background-primary);
  color: var(--text-primary);
`

const TitleWrapper = styled.div`
  margin-bottom: 16px;
`

const MessageWrapper = styled.div`
  margin-bottom: 24px;
  max-width: 400px;
`

const ErrorDetails = styled.details`
  margin-top: 20px;
  padding: 16px;
  background: var(--background-secondary);
  border-radius: 8px;
  max-width: 600px;
  width: 100%;
  text-align: left;
`

const ErrorSummary = styled.summary`
  cursor: pointer;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: 8px;
`

const ErrorStack = styled.pre`
  margin-top: 12px;
  padding: 12px;
  background: var(--background-tertiary);
  border-radius: 4px;
  overflow-x: auto;
  color: var(--text-tertiary);
  font-family:
    ui-monospace, 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Menlo,
    Consolas, 'Courier New', monospace;
  white-space: pre-wrap;
  word-break: break-word;
`

const RetryButton = styled.button`
  padding: 12px 24px;
  background: var(--accent-primary);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    transform: scale(0.98);
  }
`

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  }

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)

    // Report to Sentry
    Sentry.withScope(scope => {
      scope.setExtras({
        componentStack: errorInfo.componentStack,
        errorBoundary: true,
      })
      scope.setLevel('error')

      // Add Telegram user context if available
      if (window.Telegram?.WebApp?.initDataUnsafe?.user) {
        const tgUser = window.Telegram.WebApp.initDataUnsafe.user
        scope.setUser({
          id: String(tgUser.id),
          username: tgUser.username,
        })
      }

      Sentry.captureException(error)
    })

    this.setState({
      errorInfo,
    })
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })

    // Reload the page to reset the app state
    window.location.reload()
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return <>{this.props.fallback}</>
      }

      const isDevelopment = process.env.NODE_ENV === 'development'

      return (
        <ErrorContainer>
          <TitleWrapper>
            <Typography
              fontSize={24}
              fontWeight={600}
              color={colors.textPrimary}
            >
              <FormattedMessage
                id="error.boundary.title"
                defaultMessage="Упс! Что-то пошло не так"
              />
            </Typography>
          </TitleWrapper>
          <MessageWrapper>
            <Typography
              fontSize={16}
              fontWeight={400}
              color={colors.textSecondary}
            >
              <FormattedMessage
                id="error.boundary.message"
                defaultMessage="Произошла непредвиденная ошибка. Мы уже получили уведомление и работаем над решением."
              />
            </Typography>
          </MessageWrapper>

          <RetryButton onClick={this.handleReset}>
            <Typography fontSize={16} fontWeight={500} color={colors.white}>
              <FormattedMessage
                id="error.boundary.reload"
                defaultMessage="Перезагрузить приложение"
              />
            </Typography>
          </RetryButton>

          {isDevelopment && this.state.error && (
            <ErrorDetails>
              <ErrorSummary>
                <FormattedMessage
                  id="error.boundary.details"
                  defaultMessage="Подробности ошибки (только в режиме разработки)"
                />
              </ErrorSummary>
              <Typography fontSize={14} fontWeight={400}>
                <strong>Error:</strong> {this.state.error.toString()}
              </Typography>
              {this.state.error.stack && (
                <ErrorStack>
                  <Typography fontSize={12} color={colors.textMuted}>
                    {this.state.error.stack}
                  </Typography>
                </ErrorStack>
              )}
              {this.state.errorInfo && (
                <ErrorStack>
                  <Typography fontSize={12} color={colors.textMuted}>
                    {this.state.errorInfo.componentStack}
                  </Typography>
                </ErrorStack>
              )}
            </ErrorDetails>
          )}
        </ErrorContainer>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
